import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Content,
  Header,
  Page,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';

interface ConsentSession {
  clientName?: string;
  clientUri?: string;
  scopes: string[];
  redirectUri: string;
}

export const OAuthConsentPage = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const config = useApi(configApiRef);
  const { fetch: authFetch } = useApi(fetchApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const [session, setSession] = useState<ConsentSession | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setError(new Error('Missing session ID'));
      setLoading(false);
      return;
    }
    authFetch(`${backendUrl}/api/auth/v1/sessions/${sessionId}`)
      .then(async res => {
        if (!res.ok) {
          throw new Error(
            `Failed to load consent session: ${res.status} ${res.statusText}`,
          );
        }
        return res.json();
      })
      .then(data => setSession(data))
      .catch(err => setError(err))
      .finally(() => setLoading(false));
  }, [backendUrl, sessionId, authFetch]);

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      const res = await authFetch(
        `${backendUrl}/api/auth/v1/sessions/${sessionId}/approve`,
        { method: 'POST' },
      );
      if (!res.ok) {
        throw new Error(`Approval failed: ${res.status} ${res.statusText}`);
      }
      const { redirectUrl } = await res.json();
      window.location.href = redirectUrl;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      await authFetch(
        `${backendUrl}/api/auth/v1/sessions/${sessionId}/reject`,
        { method: 'POST' },
      );
    } catch {
      // Best-effort reject
    }

    window.close();

    if (!window.closed) {
      setSubmitting(false);
      window.location.assign('/');
    }
  };

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return (
      <Page themeId="tool">
        <Header title="Authorization Error" />
        <Content>
          <ResponseErrorPanel error={error} />
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Header title="Authorize Application" />
      <Content>
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              {session?.clientName ?? 'An application'} is requesting access
            </Typography>
            {session?.clientUri && (
              <Typography color="textSecondary" gutterBottom>
                {session.clientUri}
              </Typography>
            )}
            <Typography variant="body1" style={{ marginTop: 16 }}>
              This application wants to access your Backstage account. It will
              be able to use tools and search on your behalf.
            </Typography>
            {session?.scopes && session.scopes.length > 0 && (
              <>
                <Typography
                  variant="subtitle2"
                  style={{ marginTop: 16, marginBottom: 8 }}
                >
                  Requested permissions:
                </Typography>
                <ul>
                  {session.scopes.map(scope => (
                    <li key={scope}>
                      <Typography variant="body2">{scope}</Typography>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </CardContent>
          <CardActions>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApprove}
              disabled={submitting}
            >
              {submitting ? 'Authorizing...' : 'Authorize'}
            </Button>
            <Button onClick={handleReject} disabled={submitting}>
              Deny
            </Button>
          </CardActions>
        </Card>
      </Content>
    </Page>
  );
};
